import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { firstValueFrom } from 'rxjs';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { PermissionContextService } from '@core/auth/permission-context.service';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { LocationContextService } from '@core/tenant/location-context.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { AuthService } from '@features/auth/services/auth.service';
import { AuthShowcaseAsideComponent } from '@features/auth/presentation/components/auth-showcase-aside/auth-showcase-aside.component';
import { AuthScrollOnFocusDirective } from '@features/auth/presentation/utils/auth-scroll-on-focus.directive';
import { isStaffEmailVerified } from '@features/auth/utils/staff-email-verified.util';
import { navigateAfterStaffAuth } from '@features/auth/utils/navigate-after-staff-auth.util';
import { OtpCodeInputComponent } from '@shared/ui/otp-code-input/otp-code-input.component';
import {
  OTP_CODE_LENGTH,
  normalizeOtpDigits,
} from '@shared/utils/otp-code-digits.util';
import {
  formatOtpWait,
  isLongOtpResendWait,
} from '@shared/utils/format-otp-wait.util';
import { ToastService } from '@shared/ui/toast/toast.service';
import { MaterialModule } from 'src/app/material.module';

@Component({
  selector: 'app-verify-email',
  imports: [
    RouterModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule,
    OtpCodeInputComponent,
    AuthShowcaseAsideComponent,
    AuthScrollOnFocusDirective,
  ],
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerifyEmailComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly authSession = inject(AuthSessionService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly permissionContext = inject(PermissionContextService);

  readonly otpCodeLength = OTP_CODE_LENGTH;
  readonly submitting = signal(false);
  readonly otpError = signal(false);
  readonly otpSendError = signal(false);
  readonly otpRetryAfter = signal(0);
  readonly otpResetToken = signal(0);
  readonly code = signal('');
  readonly email = signal('');

  private otpCooldownTimer: ReturnType<typeof setInterval> | null = null;
  private autoSubmitCode: string | null = null;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.clearCooldown());
    const user = this.authSession.user();
    this.email.set(user?.email ?? '');
    if (user && isStaffEmailVerified(user)) {
      void this.navigateAfterVerified(user);
      return;
    }
    void this.requestCode();
  }

  readonly otpResendWaitKey = () =>
    isLongOtpResendWait(this.otpRetryAfter())
      ? 'auth.verifyEmail.resendWaitLong'
      : 'auth.verifyEmail.resendWait';

  readonly otpResendWaitLabel = () => formatOtpWait(this.otpRetryAfter());

  onCodeChange(value: string): void {
    const normalized = normalizeOtpDigits(value, OTP_CODE_LENGTH);
    this.code.set(normalized);
    if (normalized.length < OTP_CODE_LENGTH) {
      this.autoSubmitCode = null;
      if (this.otpError() && !this.otpSendError()) {
        this.otpError.set(false);
      }
    }
  }

  onCompleted(value: string): void {
    const normalized = normalizeOtpDigits(value, OTP_CODE_LENGTH);
    this.code.set(normalized);
    if (normalized.length !== OTP_CODE_LENGTH || this.submitting()) {
      return;
    }
    if (this.autoSubmitCode === normalized) {
      return;
    }
    this.autoSubmitCode = normalized;
    void this.verify(normalized);
  }

  resend(): void {
    if (this.otpRetryAfter() > 0 || this.submitting()) {
      return;
    }
    void this.requestCode();
  }

  signOut(): void {
    this.authService.signOut();
  }

  private async requestCode(): Promise<void> {
    this.submitting.set(true);
    this.otpSendError.set(false);
    this.otpError.set(false);
    try {
      const result = await firstValueFrom(this.authService.requestUserEmailOtp());
      if (result.retryAfterSeconds && result.retryAfterSeconds > 0) {
        this.startCooldown(result.retryAfterSeconds);
        if (!result.sent) {
          // Ya hubo un envío reciente (p. ej. al registrarse).
          return;
        }
      }
      if (!result.sent) {
        this.otpSendError.set(true);
        this.otpError.set(true);
        return;
      }
      this.otpResetToken.update((n) => n + 1);
      this.toast.successTranslated('auth.verifyEmail.codeSentToast');
    } catch {
      this.otpSendError.set(true);
      this.otpError.set(true);
    } finally {
      this.submitting.set(false);
    }
  }

  private async verify(code: string): Promise<void> {
    this.submitting.set(true);
    this.otpError.set(false);
    this.otpSendError.set(false);
    try {
      const result = await firstValueFrom(this.authService.verifyUserEmailOtp(code));
      if (!result.user || result.errors.length > 0) {
        this.otpError.set(true);
        this.otpResetToken.update((n) => n + 1);
        this.autoSubmitCode = null;
        return;
      }

      this.toast.successTranslated('auth.verifyEmail.successToast');
      await this.navigateAfterVerified(result.user);
    } catch {
      this.otpError.set(true);
      this.autoSubmitCode = null;
    } finally {
      this.submitting.set(false);
    }
  }

  private navigateAfterVerified(
    user: NonNullable<ReturnType<AuthSessionService['user']>>
  ): Promise<boolean> {
    return navigateAfterStaffAuth({
      user,
      router: this.router,
      tenantContext: this.tenantContext,
      locationContext: this.locationContext,
      adminShellMode: this.adminShellMode,
      permissionContext: this.permissionContext,
    });
  }

  private startCooldown(seconds: number): void {
    this.clearCooldown();
    this.otpRetryAfter.set(Math.max(0, Math.floor(seconds)));
    if (this.otpRetryAfter() <= 0) {
      return;
    }
    this.otpCooldownTimer = setInterval(() => {
      const next = this.otpRetryAfter() - 1;
      this.otpRetryAfter.set(Math.max(0, next));
      if (next <= 0) {
        this.clearCooldown();
      }
    }, 1000);
  }

  private clearCooldown(): void {
    if (this.otpCooldownTimer) {
      clearInterval(this.otpCooldownTimer);
      this.otpCooldownTimer = null;
    }
  }
}
